import React, { useState, useEffect } from 'react';
import { userService, talentService, authService } from './../../api/apiService';
import edit from './../../images/edit.svg';
import resetPw from './../../images/reset-pw.svg';
import Select, { components } from 'react-select';

function UserDataForm() {
  const [users, setUsers] = useState([]);
  const [talents, setTalents] = useState([]);
  const [currentUserEditing, setCurrentUserEditing] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editTalentIds, setEditTalentIds] = useState([]);
  const [selectedTalentToAdd, setSelectedTalentToAdd] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  
  useEffect(() => {
    // Fetch users and talents
    fetchUsers();
    fetchTalents();
  }, []);

  // Fetch all users with their talents
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Use the new endpoint that includes talents with users
      const response = await userService.getAllUsersWithTalents();
      
      // Extract the users array from the nested response structure
      const userData = response.data?.users || [];
      console.log('Fetched users with talents:', userData);
      setUsers(userData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get talent name by ID from user's talents array
  const getTalentName = (user, talentId) => {
    // Check if user has a talents array
    if (user.talents && Array.isArray(user.talents)) {
      // Find the talent with matching ID
      const talent = user.talents.find(t => t.id === talentId);
      if (talent) return talent.name;
    }
    
    // Fall back to the talents state if not found in user's talents
    const talent = talents.find(t => t.id === talentId);
    return talent?.name || `Talent ${talentId}`;
  };

  // Fetch all talents
  const fetchTalents = async () => {
    try {
      const response = await talentService.getAllTalents(1, 1000);
      const talentsData = response.data?.talents || [];
      
      // Format the talents data to ensure name is always available
      const formattedTalents = talentsData.map(talent => ({
        ...talent,
        // Make sure name is available (fall back to function or id if not)
        name: talent.name || talent.Name || talent.function || talent.Funktion || `Talent ${talent.id}`
      }));
      
      setTalents(formattedTalents);
    } catch (error) {
      console.error('Error fetching talents:', error);
    }
  };

  const handleEdit = (user) => {
    setEditEmail(user.email);
    setEditRole({ value: user.role, label: user.role });
    
    // Get talent IDs from the user's talents array
    const talentIdList = user.talents?.map(talent => talent.id) || [];
    setEditTalentIds(talentIdList);
    
    setCurrentUserEditing(user.id);
    setIsEditMode(true);
    setEditingUserId(user.id);
  };

  const handleCancelEdit = () => {
    setCurrentUserEditing(null);
    setIsEditMode(false);
    setEditingUserId(null);
    setSelectedTalentToAdd(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // First update basic user information
      const userData = {
        email: editEmail,
        role: editRole.value
      };
      
      console.log('Updating user with data:', userData);
      console.log('Updating talents with IDs:', editTalentIds);
      
      // Update basic user information
      await userService.updateUser(currentUserEditing, userData);
      
      // Then update the talents with the correct payload format
      await userService.updateUserTalents(currentUserEditing, { 
        talent_ids: editTalentIds
      });
      
      alert('User updated successfully!');
      setIsEditMode(false);
      setEditingUserId(null);
      setSelectedTalentToAdd(null);
      setError(null); // Clear any errors
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error updating user:', error);
      setError(`Failed to update user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to add a single talent to a user
  const handleAddSingleTalent = async () => {
    if (!selectedTalentToAdd || !currentUserEditing) return;
    
    setLoading(true);
    try {
      // Use the POST endpoint to add a single talent with correct payload format
      await userService.addTalentToUser(currentUserEditing, { 
        talent_id: selectedTalentToAdd.value
      });
      
      // Update the local state to reflect the addition
      setEditTalentIds(prev => [...prev, selectedTalentToAdd.value]);
      setSelectedTalentToAdd(null);
      
      alert('Talent added successfully!');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error adding talent to user:', error);
      setError(`Failed to add talent: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to remove a talent from a user
  const handleRemoveTalent = async (talentId) => {
    if (!currentUserEditing) return;
    
    if (!window.confirm('Are you sure you want to remove this talent from the user?')) {
      return;
    }
    
    setLoading(true);
    try {
      // Remove the talent from the local state first
      setEditTalentIds(prev => prev.filter(id => id !== talentId));
      
      // Update the user's talents through the API
      const updatedTalentIds = editTalentIds.filter(id => id !== talentId);
      await userService.updateUserTalents(currentUserEditing, { 
        talent_ids: updatedTalentIds
      });
      
      alert('Talent removed successfully!');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error removing talent from user:', error);
      setError(`Failed to remove talent: ${error.message}`);
      // Revert the local state change on error
      setEditTalentIds(prev => [...prev, talentId]);
    } finally {
      setLoading(false);
    }
  };

  const DropdownIndicator = props => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <div style={{ width: '16px', margin: '0' }}>
            <svg version="1.1" id="Isolationsmodus" x="0px" y="0px" viewBox="0 0 322.9 257.78" shapeRendering="geometricPrecision">
              <path xmlns="http://www.w3.org/2000/svg" d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z" />
            </svg>
          </div>
        </components.DropdownIndicator>
      )
    );
  };



  // Handle password reset using the new admin endpoint
  const handleResetPassword = async (userId) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password to "123456"?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/auth/reset-user-password/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Password has been reset to "123456"');
      } else {
        alert(result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password. Please try again.');
    }
  };

  // Handle user deletion using the dedicated endpoint
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await userService.deleteUser(userId);
      alert('User deleted successfully');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      height: '50px',
      width: '400px',
      margin: '0px',
      borderRadius: '0px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'black' : 'inherit',
      '&:hover': { 
        backgroundColor: state.isSelected ? 'black' : '#f7f7f7',
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
  };
  
  const userRoles = [
    { label: 'talent', value: 'talent' },
    { label: 'Super Admin', value: 'superadmin' },
    { label: 'Planning Orendt', value: 'planning' },
    { label: 'Set Cast', value: 'setCastEditor' },
  ];

  const handleSelectRole = (selectedOption) => {
    setEditRole(selectedOption);
  };

  const filteredUsers = Array.isArray(users) 
    ? users.filter((user) => {
        const matchesRole = selectedRoleFilter ? user.role === selectedRoleFilter.value : true;
        const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
        return matchesRole && matchesSearch;
      }) 
    : [];

  // Update the talent select dropdown formatting
  const formatTalentOption = (talent) => {
    // Make sure we have all talent details for display
    const talentName = talent.name || talent.Name || '';
    const talentFunction = talent.function || talent.Funktion || '';
    const talentType = talent.type || talent.bereich || talent.Bereich || '';
    
    // Format the label with name and function if available
    if (talentFunction) {
      return {
        label: `${talentName} (${talentFunction}${talentType ? ` - ${talentType}` : ''})`,
        value: talent.id
      };
    }
    
    return {
      label: talentName,
      value: talent.id
    };
  };

  if (loading && (!users || users.length === 0)) {
    return <div className="content-wrapper">Loading users...</div>;
  }

  if (error && (!users || users.length === 0)) {
    return <div className="content-wrapper">Error: {error}</div>;
  }

  return (
    <div className="content-wrapper page">
      <div className='content-wrapper'>
        <div style={{ justifyContent: 'space-between', display: 'flex', marginBottom: '30px', alignItems: 'center' }}>
          <h1>Users</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <a
              href="/admin/talent-groups"
              className='big-button'
              style={{ backgroundColor: '#000', color: '#fff', textDecoration: 'none' }}
            >
              Manage Talent Groups
            </a>
            <a 
              href="/admin/new-user-registration" 
              className='big-button'
            >
              Create User
            </a>
          </div>
        </div>
        

      </div>
      
      <div className='content-wrapper content-wrapper-talent-list'>
        <div style={{ display: 'flex', marginBottom: '20px',alignItems:'center' }} className='users-container'>
          <input
            type="text"
            className='inputtext search'
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginRight: '10px', flexGrow: 1 }}
          />
          <Select
            placeholder="Filter by role"
            value={selectedRoleFilter}
            onChange={(selectedOption) => setSelectedRoleFilter(selectedOption)}
            options={userRoles}
            styles={customStyles}
            isClearable
          />
        </div>

        <div style={{ marginTop: '30px' }} className='list-tasks-header'>
          <div style={{ flexBasis: '350px' }}>Email</div>
          <div style={{ flexBasis: '300px' }}>Managed Talents</div>
          <div style={{ flexBasis: '150px' }}>Role</div>
          <div style={{ flexBasis: '120px' }}>Actions</div>
        </div>
        
        <div style={{ marginTop: '0' }} className='users-container'>
          {filteredUsers.map(user => (
            <div key={user.id}>
              <div className='list-tasks-item'>
                <div style={{ flexBasis: '350px' }}>{user.email}</div>
                <div style={{ flexBasis: '300px' }}>
                  {user.talents && Array.isArray(user.talents) && user.talents.map(talent => (
                    <span style={{ display: 'block' }} key={talent.id}>
                      {talent.name} ({talent.Funktion})
                    </span>
                  ))}
                </div>
                <div style={{ flexBasis: '150px' }}>{user.role}</div>
                <div style={{ flexBasis: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                  <button 
                    onClick={() => handleEdit(user)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      padding: '5px'
                    }}
                    title="Edit user"
                  >
                    <img src={edit} alt="Edit" style={{ width: '20px', height: '20px' }} />
                  </button>
                  <button 
                    onClick={() => handleResetPassword(user.id)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      padding: '5px'
                    }}
                    title="Reset password to 123456"
                  >
                    <img src={resetPw} alt="Reset Password" style={{ width: '20px', height: '20px' }} />
                  </button>
                </div>
              </div>

              {/* Edit form */}
              {currentUserEditing === user.id && (
                <div style={{ padding: '10px 20px' }}>
                  <form onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
                    <label>
                      <div style={{ fontWeight: '500', fontSize: '1rem', lineHeight: '1.5', textTransform: 'none', marginTop: '10px', marginBottom: '5px' }}>
                        User role
                      </div>
                      <div style={{ marginBottom: '20px' }}>
                        <Select
                          components={{ DropdownIndicator }}
                          options={userRoles}
                          onChange={handleSelectRole}
                          value={editRole}
                          styles={customStyles}
                        />
                      </div>
                    </label>

                    <div style={{ fontWeight: '500', fontSize: '1rem', lineHeight: '1.5', textTransform: 'none', marginTop: '10px', marginBottom: '5px' }}>
                      Managed Talents
                    </div>
                    
                    {/* Display current talents with remove buttons */}
                    <div style={{ marginBottom: '15px' }}>
                      {editTalentIds.length > 0 ? (
                        editTalentIds.map(talentId => {
                          // Find the talent in the user's talents array first
                          const userTalent = user.talents?.find(t => t.id === talentId);
                          const talent = userTalent || talents.find(t => t.id === talentId);
                          const talentName = talent ? (talent.name || talent.Name || `Talent ${talentId}`) : `Talent ${talentId}`;
                          const talentFunction = talent ? (talent.function || talent.Funktion || '') : '';
                          
                          return (
                            <div key={talentId} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '8px 12px',
                              margin: '5px 0',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}>
                              <span>
                                {talentName} {talentFunction && `(${talentFunction})`}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveTalent(talentId)}
                                disabled={loading}
                                style={{
                                  background: '#ff4444',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ 
                          padding: '10px', 
                          backgroundColor: '#f9f9f9', 
                          borderRadius: '4px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          No talents assigned
                        </div>
                      )}
                    </div>

                    {/* Add single talent section */}
                    <div style={{ marginTop: '20px', marginBottom: '10px', border: '1px solid #eee', borderRadius: '5px' }}>
                      <div style={{ fontWeight: '500', fontSize: '1rem', lineHeight: '1.5', textTransform: 'none', marginBottom: '10px' }}>
                        Add Single Talent
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flexGrow: 1 }}>
                          <Select
                            autoComplete="off"
                            value={selectedTalentToAdd}
                            onChange={setSelectedTalentToAdd}
                            options={talents
                              .filter(talent => !editTalentIds.includes(talent.id))
                              .map(formatTalentOption)}
                            styles={customStyles}
                            placeholder="Select a talent to add"
                          />
                        </div>
                        <button 
                          type="button" 
                          className="button" 
                          onClick={handleAddSingleTalent}
                          disabled={!selectedTalentToAdd || loading}
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px', marginBottom: '40px' }}>
                      <button className='big-button' type="submit" disabled={loading}>
                        {loading ? 'Saving...' : 'Save All Changes'}
                      </button>
                      <button className='big-button' type="button" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default UserDataForm;
